import { ElementRef } from '@angular/core';
import sanitizeHtml from 'sanitize-html';
import { encode } from 'html-entities';
import { LocalSettingsKey } from '@shared/types/local-settings';
import * as moment from 'moment';

export class UtilService {
  constructor() {}

  public static jsonToQueryString(json: any): string {
    return Object.keys(json)
      .map((key, index) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(json[key]);
      })
      .join('&');
  }

  public static clearAmpersand(content: string): string {
    return content.indexOf('&amp;') === -1 ? content : content.replace(/&amp;/g, '&');
  }

  public static clearContent(content: string): string {
    if (content === null) {
      return '';
    }
    content = content.replace(/<!--[\s\S]*?-->/g, '');
    return content;
  }

  public static clearImportantStyle(content: string): string {
    if (content === null) {
      return '';
    }
    content = content.replace(/!important/g, '');
    return content;
  }

  public static sanitizeHTML(text: string): string {
    const sanitized = sanitizeHtml(text, {
      allowedTags: [
        'h1',
        'h2',
        'span',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'p',
        'a',
        'ul',
        'ol',
        'u',
        'nl',
        'li',
        'b',
        'i',
        'strong',
        'em',
        'strike',
        'code',
        'hr',
        'br',
        'div',
        'table',
        'thead',
        'caption',
        'tbody',
        'tr',
        'th',
        'td',
        'pre',
        'img',
        'font',
        'center',
        'title',
        'button',
        's',
        'del',
        'ins',
        'map',
        'area',
      ],
      allowedAttributes: {
        span: ['title'],
        a: ['href', 'name', 'target'],
        // We don't currently allow img itself by default, but this
        // would make sense if we did. You could add srcset here,
        // and if you do the URL is checked for safety
        img: ['src', 'srcset'],
        '*': [
          'style',
          'align',
          'alt',
          'center',
          'bgcolor',
          'color',
          'width',
          'height',
          'border',
          'cellspacing',
          'cellpadding',
          'rel',
          'valign',
          'nowrap',
          'colspan',
          'cellpadding',
          'cellspacing',
          'class',
          'rowspan',
          'start',
          'id',
          'cid',
          'href',
          'name',
          'target',
          'coords',
          'shape',
          'usemap',
        ],
      },
      allowedSchemesByTag: {
        img: ['http', 'https', 'data', 'blob'],
      },
    });

    return sanitized;
  }

  public static removeTableAlignment(text: string): string {
    text = text.replace(/align=\"left\"/g, '');
    text = text.replace(/align=\"right\"/g, '');
    return text;
  }

  public static sanitizeAndEncodeHTML(text: string): string {
    return this.clearAmpersand(encode(this.sanitizeHTML(text)));
  }

  public static formatBytes(bytes, decimals = 2): string {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // Scroll list to top after paging/refreshing/sorting
  // tslint:disable-next-line:member-ordering
  public static scrollToTop(list: ElementRef): void {
    if (list && list.nativeElement.scrollTop > 0) {
      list.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  // tslint:disable-next-line:member-ordering
  public static makeResizableDiv(div, rightResizeContent) {
    const element = div;
    let width = element.offsetWidth;
    let half = (window.innerWidth - element.getBoundingClientRect().left) / 2;
    window.addEventListener('resize', () => {
      half = (window.innerWidth - element.getBoundingClientRect().left) / 2;
      element.style.width = '';
      rightResizeContent.nativeElement.style.width = '';
      localStorage.removeItem(LocalSettingsKey.RESIZE);
    });
    const resizer = document.getElementById('resizer');

    if (localStorage.getItem(LocalSettingsKey.RESIZE)) {
      element.style.width = JSON.parse(localStorage.getItem(LocalSettingsKey.RESIZE)).left;
      rightResizeContent.nativeElement.style.width = JSON.parse(localStorage.getItem(LocalSettingsKey.RESIZE)).right;
    }
    resizer.addEventListener('mousedown', function (e) {
      e.preventDefault();
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResize);
    });
    function resize(e) {
      const resizeWidth = e.pageX - element.getBoundingClientRect().left;
      const resizeWidthPercent = (resizeWidth * 100) / (window.innerWidth - element.getBoundingClientRect().left);
      if (width < resizeWidth && resizeWidth < half) {
        element.style.width = resizeWidthPercent + '%';
        rightResizeContent.nativeElement.style.width = 100 - resizeWidthPercent + '%';
      }
    }
    function stopResize(e) {
      const resizeValues = {
        left: element.style.width,
        right: rightResizeContent.nativeElement.style.width,
      };
      localStorage.setItem(LocalSettingsKey.RESIZE, JSON.stringify(resizeValues));
      window.removeEventListener('mousemove', resize);
    }
  }

  public static makeResizableHorizontalDiv(div, bottomResizeContent) {
    const element = div;
    const height = element.offsetHeight;
    const resizer = document.getElementById('resizer-horizontal');

    if (localStorage.getItem(LocalSettingsKey.RESIZE_HORIZONTAL)) {
      element.style.height = JSON.parse(localStorage.getItem(LocalSettingsKey.RESIZE_HORIZONTAL)).top;
      bottomResizeContent.nativeElement.style.height = JSON.parse(localStorage.getItem(LocalSettingsKey.RESIZE_HORIZONTAL)).bottom;
    }
    resizer.addEventListener('mousedown', function (e) {
      e.preventDefault();
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResize);
    });
    function resize(e) {
      const half = (window.innerHeight - element.getBoundingClientRect().top) * 0.75;
      const resizeHeight = e.pageY - element.getBoundingClientRect().top;
      const resizeHeightPercent = (resizeHeight * 100) / (window.innerHeight - element.getBoundingClientRect().top);
      if (height < resizeHeight && resizeHeight < half) {
        element.style.height = resizeHeightPercent + '%';
        bottomResizeContent.nativeElement.style.height = 100 - resizeHeightPercent + '%';
      }
    }

    function stopResize(e) {
      const resizeValues = {
        top: element.style.height,
        bottom: bottomResizeContent.nativeElement.style.height,
      };
      localStorage.setItem(LocalSettingsKey.RESIZE_HORIZONTAL, JSON.stringify(resizeValues));
      window.removeEventListener('mousemove', resize);
    }
  }

  public static arrayEquals(a, b) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
  }

  public static handlePositiveNumbers(event) {
    return event.charCode == 8 || event.charCode == 0 ? null : event.charCode >= 48 && event.charCode <= 57;
  }

  public static closeWindow(): void {
    setTimeout(() => {
      window.close();
    }, 1000);
  }

  public static generateUUID(): string {
    const s = [];
    const hexDigits = '0123456789abcdef';
    for (let i = 0; i < 36; i++) {
      s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
    // tslint:disable-next-line:no-bitwise
    s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
    s[8] = s[13] = s[18] = s[23] = '-';

    const uuid = s.join('');
    return uuid;
  }

  public static capitalizeString(text: string): string {
    if (!text) {
      return null;
    }
    const arr = text.toLowerCase().split(' ');

    for (let i = 0; i < arr.length; i++) {
      arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
    }
    return arr.join(' ');
  }

  public static logInfo(info: string[]): void {
    console.log(
      '%cMailim',
      'color:#0469d0;border:1px solid #0469d0;padding:1px 2px;border-radius: 2px',
      `[${moment(Date.now()).format('DD/MM/YYYY HH:mm:ss')}]`,
      ...info
    );
  }

  public static logError(error: string[]): void {
    console.error(
      '%cMailim',
      'color:#f05f6d;border:1px solid #f05f6d;padding:1px 2px;border-radius: 2px',
      `[${moment(Date.now()).format('DD/MM/YYYY HH:mm:ss')}]`,
      ...error
    );
  }

  public static handleMouseMove(event) {
    let eventDoc, doc, body;
    event = event || window.event; // IE-ism

    // If pageX/Y aren't available and clientX/Y are,
    // calculate pageX/Y - logic taken from jQuery.
    // (This is to support old IE)
    if (event.pageX == null && event.clientX != null) {
      eventDoc = (event.target && event.target.ownerDocument) || document;
      doc = eventDoc.documentElement;
      body = eventDoc.body;

      event.pageX =
        event.clientX + ((doc && doc.scrollLeft) || (body && body.scrollLeft) || 0) - ((doc && doc.clientLeft) || (body && body.clientLeft) || 0);
      event.pageY =
        event.clientY + ((doc && doc.scrollTop) || (body && body.scrollTop) || 0) - ((doc && doc.clientTop) || (body && body.clientTop) || 0);
    }
    const position = {
      x: event.pageX,
      y: event.pageY,
    };
    return position;
  }

  public static extractURL(text: string): string {
    var urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
    return text.replace(urlRegex, function (url) {
      return url;
    });
  }

  public static isMailTo(url: string): boolean {
    return /^mailto:/i.test(url);
  }

  public static appendBackdrop(): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade show';
    document.body.appendChild(backdrop);
  }

  public static removeBackdrop(): void {
    const backdrop = document.querySelector('.modal-backdrop');
    if (backdrop) {
      backdrop.remove();
    }
  }
}
